import { Injectable, signal } from '@angular/core';

export type AppTheme = 'cobalt' | 'indigo' | 'emerald';
export type ColorMode = 'light' | 'dark';

export type ThemeOption = {
  key: AppTheme;
  label: string;
  description: string;
};

export type ColorModeOption = {
  key: ColorMode;
  label: string;
};

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly themeStorageKey = 'meetrix-theme';
  private readonly colorModeStorageKey = 'meetrix-color-mode';

  readonly themeOptions: ThemeOption[] = [
    {
      key: 'cobalt',
      label: 'Cobalt',
      description: 'Crisp high-contrast blue',
    },
    {
      key: 'indigo',
      label: 'Indigo',
      description: 'Balanced neutral workspace',
    },
    {
      key: 'emerald',
      label: 'Emerald',
      description: 'Fresh modern green',
    },
  ];

  readonly colorModeOptions: ColorModeOption[] = [
    { key: 'light', label: 'Light' },
    { key: 'dark', label: 'Dark' },
  ];

  readonly currentTheme = signal<AppTheme>('indigo');
  readonly currentColorMode = signal<ColorMode>('light');

  constructor() {
    this.initializePreferences();
  }

  setTheme(theme: AppTheme): void {
    this.currentTheme.set(theme);
    this.applyTheme(theme);
    this.persistTheme(theme);
  }

  setColorMode(mode: ColorMode): void {
    this.currentColorMode.set(mode);
    this.applyColorMode(mode);
    this.persistColorMode(mode);
  }

  private initializePreferences(): void {
    const theme = this.readStoredTheme();
    const mode = this.readStoredColorMode();

    this.currentTheme.set(theme);
    this.currentColorMode.set(mode);

    this.applyTheme(theme);
    this.applyColorMode(mode);
  }

  private readStoredTheme(): AppTheme {
    if (typeof window === 'undefined') {
      return 'indigo';
    }

    const storedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (this.isAppTheme(storedTheme)) {
      return storedTheme;
    }

    const migratedTheme = this.migrateLegacyTheme(storedTheme);
    if (migratedTheme) {
      return migratedTheme;
    }

    return 'indigo';
  }

  private persistTheme(theme: AppTheme): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.themeStorageKey, theme);
  }

  private applyTheme(theme: AppTheme): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.setAttribute('data-theme', theme);
  }

  private readStoredColorMode(): ColorMode {
    if (typeof window === 'undefined') {
      return 'light';
    }

    const storedMode = window.localStorage.getItem(this.colorModeStorageKey);
    if (this.isColorMode(storedMode)) {
      return storedMode;
    }

    return 'light';
  }

  private persistColorMode(mode: ColorMode): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(this.colorModeStorageKey, mode);
  }

  private applyColorMode(mode: ColorMode): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.setAttribute('data-color-mode', mode);
  }

  private isAppTheme(value: string | null): value is AppTheme {
    return value === 'cobalt' || value === 'indigo' || value === 'emerald';
  }

  private isColorMode(value: string | null): value is ColorMode {
    return value === 'light' || value === 'dark';
  }

  private migrateLegacyTheme(value: string | null): AppTheme | null {
    if (value === 'linear' || value === 'core') {
      return 'indigo';
    }
    if (value === 'teams' || value === 'ocean') {
      return 'emerald';
    }
    if (value === 'slack' || value === 'dusk') {
      return 'cobalt';
    }

    return null;
  }
}
